import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AlertService } from '../../../../services/alert.service';
import { SettingsFacadeService } from '../../../../services/facades/settings-facade.service';

export type SidebarButtonName =
  | 'back'
  | 'toggle_menu'
  | 'tasks'
  | 'my_lists'
  | 'global_search'
  | 'refresh_access'
  | 'message'
  | 'settings'
  | 'divider'
  | 'admin'
  | 'chat'
  | 'logout'
  | 'start'
  | 'dashboard'
  | 'organization'
  | 'workflow';

export type SidebarButton = {
  name: SidebarButtonName;
  label: string;
  disabled: boolean;
  link?: string;
  svg?: string;
  icon_name?: string;
};

@Component({
  selector: 'app-sidebar-button',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatListModule, TranslateModule],
  templateUrl: './sidebar-button.component.html',
  styleUrls: ['./sidebar-button.component.scss'],
})
export class SidebarButtonComponent {
  @Input() button!: SidebarButton;
  @Input() isExpanded: boolean = false;
  @Output() buttonClick = new EventEmitter<SidebarButton>();

  private settingsFacadeService = inject(SettingsFacadeService);
  private translateService = inject(TranslateService);
  private alertService = inject(AlertService);
  private router = inject(Router);

  async onClick(event: MouseEvent): Promise<void> {
    event.preventDefault();

    if (this.button.disabled) {
      return;
    }

    if (this.button.name === 'logout') {
      const demoModeActive = await firstValueFrom(
        this.settingsFacadeService.settings$.pipe(
          take(1),
          map(settings => settings.demoMode),
        ),
      );

      if (demoModeActive) {
        const alertText = this.translateService.instant('GENERAL.logout_blocked');
        await this.alertService.show({
          message: alertText,
          buttons: [{ role: 'cancel', text: 'OK' }],
        });
      } else {
        this.buttonClick.emit(this.button);

        setTimeout(() => {
          this.router.navigate(['/logout']);
        }, 10);
      }
      return;
    }

    this.buttonClick.emit(this.button);

    if (this.button.link) {
      setTimeout(() => {
        this.router.navigate([this.button.link], {
          skipLocationChange: false,
          replaceUrl: false,
        });
      }, 10);
    }
  }

  isActive(): boolean {
    if (!this.button.link || this.button.disabled) {
      return false;
    }

    const buttonPath = this.button.link.startsWith('/')
      ? this.button.link.substring(1)
      : this.button.link;
    const currentUrl = this.router.url;

    return currentUrl.includes(buttonPath);
  }
}
